import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { Skeleton } from 'components/ui/skeleton';
import { HISTORY_PAGE_SIZE } from '../constants/credits';
import { SectionError } from './section-error';

export interface HistoryRow {
  id: string;
  title: string;
  subtitle: string;
  value?: string;
  badge?: ReactNode;
  action?: ReactNode;
}

interface HistoryListProps {
  rows: HistoryRow[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  emptyText: string;
  page?: number;
  total?: number;
  onPageChange?: (page: number) => void;
}

/** One list for top-ups, credit use and invoices, so each state looks the same. */
export const HistoryList = ({
  rows,
  isLoading,
  isError,
  onRetry,
  emptyText,
  page = 1,
  total = 0,
  onPageChange,
}: HistoryListProps) => {
  const { t } = useTranslation();
  const pageCount = Math.max(1, Math.ceil(total / HISTORY_PAGE_SIZE));

  if (isLoading) {
    return (
      <div className="space-y-2" aria-busy="true">
        {[0, 1, 2].map((key) => (
          <Skeleton key={key} className="h-14 w-full" />
        ))}
      </div>
    );
  }
  if (isError) {
    return (
      <SectionError
        message={t('HISTORY_LOAD_FAILED', 'This list could not be loaded.')}
        onRetry={onRetry}
      />
    );
  }
  if (!rows.length) {
    return (
      <p className="rounded-lg border border-dashed p-4 text-center text-xs text-muted-foreground sm:text-sm">
        {emptyText}
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <ul className="divide-y rounded-lg border">
        {rows.map((row) => (
          <li
            key={row.id}
            className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-3 py-2.5"
          >
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">{row.title}</p>
              <p className="text-xs text-muted-foreground">{row.subtitle}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {row.value && <span className="text-sm font-medium tabular-nums">{row.value}</span>}
              {row.badge}
              {row.action}
            </div>
          </li>
        ))}
      </ul>
      {onPageChange && pageCount > 1 && (
        <nav
          aria-label={t('PAGINATION', 'Pagination')}
          className="flex items-center justify-between gap-2"
        >
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
          >
            {t('PREVIOUS', 'Previous')}
          </Button>
          <span className="text-xs text-muted-foreground" aria-live="polite">
            {t('PAGE_OF', { page, total: pageCount, defaultValue: 'Page {{page}} of {{total}}' })}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= pageCount}
            onClick={() => onPageChange(page + 1)}
          >
            {t('NEXT', 'Next')}
          </Button>
        </nav>
      )}
    </div>
  );
};
